'use client'

import React, { useMemo } from 'react'
import {
  Table,
  Skeleton,
  Checkbox,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  LinearProgress,
  TableSortLabel,
  TablePagination,
} from '@mui/material'

import { StyledTableContainer, StyledTableHead } from './styled'

export const CommonTable = ({
  columns,
  data,
  style,
  loading = false,
  pageSizeOptions = [10, 20, 50],
  totalItems = 0,
  showSelect = false,
  mode = 'server',
  noPagination = false,
  rowHeight = '25px',
  pagination = {
    page: 0,
    perPage: pageSizeOptions[0],
    sortBy: undefined,
    sortDirection: undefined,
  },
  refetch,
  selectedRows = [],
  onPaginationChange,
  onRowClick,
  onSelectionChange,
}) => {
  const handlePageChange = (event, newPage) => {
    if (onPaginationChange) {
      onPaginationChange({ ...pagination, page: newPage })
      if (onSelectionChange) onSelectionChange([])
    }
  }

  const handlePerPageChange = event => {
    const newPerPage = parseInt(event.target.value, 10)
    if (onPaginationChange) {
      onPaginationChange({ ...pagination, perPage: newPerPage, page: 0 })
      if (onSelectionChange) onSelectionChange([])
    }
  }

  const handleSort = columnId => {
    const isAsc =
      pagination.sortBy === columnId && pagination.sortDirection === 'asc'
    const newSortDirection = isAsc ? 'desc' : 'asc'
    if (onPaginationChange) {
      onPaginationChange({
        ...pagination,
        sortBy: columnId,
        sortDirection: newSortDirection,
      })
      if (onSelectionChange) onSelectionChange([])
    }
  }

  const findIndex = item => {
    const index = selectedRows.indexOf(item)
    return index
  }

  const handleSelectRow = row => {
    const index = findIndex(row)
    const exists = index !== -1
    const newSelectedRows = exists
      ? selectedRows.filter((_, i) => i !== index)
      : [...selectedRows, row]

    if (onSelectionChange) onSelectionChange(newSelectedRows)
  }

  const handleBulkSelect = () => {
    const newSelected =
      selectedRows.length === data.length ? [] : data.map(row => row)
    if (onSelectionChange) onSelectionChange(newSelected)
  }

  // Sort and paginate data for client mode
  const displayData = useMemo(() => {
    let processedData = [...data]

    // Sort data if in client mode and sortBy is specified
    if (mode === 'client' && pagination.sortBy && columns.length > 0) {
      const sortColumn = columns.find(col => col.id === pagination.sortBy)
      if (sortColumn) {
        processedData.sort((a, b) => {
          // Use sortValue if provided (can be a function or key path)
          let aVal, bVal
          if (sortColumn.sortValue) {
            if (typeof sortColumn.sortValue === 'function') {
              aVal = sortColumn.sortValue(a)
              bVal = sortColumn.sortValue(b)
            } else {
              aVal = a[sortColumn.sortValue]
              bVal = b[sortColumn.sortValue]
            }
          } else {
            aVal = a[pagination.sortBy]
            bVal = b[pagination.sortBy]
          }

          // Handle custom sort function if provided
          if (sortColumn.sortFn) {
            return sortColumn.sortFn(a, b, pagination.sortDirection)
          }

          // Handle null/undefined values
          if (aVal == null && bVal == null) return 0
          if (aVal == null) return 1
          if (bVal == null) return -1

          // Handle numeric values
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return pagination.sortDirection === 'asc'
              ? aVal - bVal
              : bVal - aVal
          }

          // Handle date strings or Date objects
          if (
            (typeof aVal === 'string' &&
              typeof bVal === 'string' &&
              (aVal.match(/^\d{2}\/\d{2}\/\d{4}$/) ||
                aVal.match(/^\d{4}-\d{2}-\d{2}/))) ||
            aVal instanceof Date ||
            bVal instanceof Date
          ) {
            const aDate =
              aVal instanceof Date ? aVal.getTime() : new Date(aVal).getTime()
            const bDate =
              bVal instanceof Date ? bVal.getTime() : new Date(bVal).getTime()
            return pagination.sortDirection === 'asc'
              ? aDate - bDate
              : bDate - aDate
          }

          // Handle string comparison
          const comparison = String(aVal).localeCompare(String(bVal))
          return pagination.sortDirection === 'asc' ? comparison : -comparison
        })
      }
    }

    // Paginate data if in client mode and pagination is enabled
    if (mode === 'client' && !noPagination) {
      const startIndex = pagination.page * pagination.perPage
      const endIndex = startIndex + pagination.perPage
      return processedData.slice(startIndex, endIndex)
    }

    return processedData
  }, [data, mode, noPagination, pagination, columns])

  const isLoadingAndEmpty = loading && !data.length
  const isLoadingWithContent = loading && data.length

  return (
    <StyledTableContainer style={style}>
      <Table>
        <StyledTableHead>
          <TableRow style={{ position: 'relative' }}>
            {showSelect && (
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={
                    !loading &&
                    !!selectedRows.length &&
                    selectedRows.length === data.length
                  }
                  indeterminate={
                    !!selectedRows.length && selectedRows.length < data.length
                  }
                  onChange={handleBulkSelect}
                />
              </TableCell>
            )}
            {columns.map(column => (
              <TableCell
                key={column.id}
                align={column.align}
                style={{ minWidth: column.minWidth }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    style={{ whiteSpace: 'nowrap' }}
                    active={pagination.sortBy === column.id}
                    direction={
                      pagination.sortBy === column.id
                        ? pagination.sortDirection
                        : 'asc'
                    }
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  <div style={{ whiteSpace: 'nowrap' }}>{column.label}</div>
                )}
              </TableCell>
            ))}
            {isLoadingWithContent ? (
              <TableCell
                colSpan={columns.length + (showSelect ? 1 : 0)}
                style={{ padding: 0, border: 'none', height: 0 }}
              >
                <LinearProgress
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                  }}
                />
              </TableCell>
            ) : null}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {isLoadingAndEmpty ? (
            Array.from({
              length: pagination.perPage < 10 ? pagination.perPage : 10,
            }).map((_, index) => (
              <TableRow key={index}>
                {columns.map(column => (
                  <TableCell key={column.id}>
                    <Skeleton variant='text' width='80%' height={20} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : displayData.length > 0 ? (
            displayData.map((row, rowIndex) => (
              <TableRow
                hover
                key={rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {showSelect && (
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={findIndex(row) !== -1}
                      onClick={event => event.stopPropagation()}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                )}
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ height: rowHeight }}
                  >
                    {column.render
                      ? column.render({
                          row,
                          refetch,
                          id: row[column.id],
                        })
                      : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (showSelect ? 1 : 0)}
                align='center'
              >
                <Typography variant='body2' color='textSecondary'>
                  No records to display
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isLoadingAndEmpty && !noPagination && (
        <TablePagination
          rowsPerPageOptions={pageSizeOptions}
          component='div'
          disabled={loading}
          count={mode === 'server' ? totalItems : data.length}
          rowsPerPage={pagination.perPage}
          page={pagination.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerPageChange}
        />
      )}
    </StyledTableContainer>
  )
}
